import {
  Autocomplete,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getDirectlyObservedTherapyByTherapyQueryKey,
  useUpdateDirectlyObservedTherapyByPatient,
  type DirectlyObservedTherapyEntryDto,
} from '../../../../../api/created/directlyObservedTherapy.ts';
import { getGetTherapyByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { formatDateForInput, formatDateWithGermanMonth, getDateAfterInputValue } from '../../../../../utils/date.ts';
import {
  DOT_FREQUENCY_OPTIONS,
  toDotFrequencyDisplayLabel,
  toDotFrequencyPayload,
} from '../../../../../utils/dotFrequency.ts';
import { PatientAddressDisplay } from '../../../../components/patient-address/PatientAddressDisplay.tsx';

interface FormValues {
  until: string;
  frequenz: string;
}

export function EndDirectlyObservedTherapyDialog({
  open,
  onClose,
  entry,
  patientId,
  therapyId,
}: {
  open: boolean;
  onClose: () => void;
  entry: DirectlyObservedTherapyEntryDto;
  patientId: string;
  therapyId: string;
}) {
  const executor = entry.executor;
  const applicant = entry.applicant;
  const startDate = entry.from ?? executor?.from;
  const startDateForMin = startDate ? getDateAfterInputValue(formatDateForInput(startDate)) : undefined;

  const queryClient = useQueryClient();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      until: entry.until ?? executor?.until ?? '',
      frequenz: toDotFrequencyDisplayLabel(entry.frequenz),
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const updateDirectlyObservedTherapyMutation = useUpdateDirectlyObservedTherapyByPatient({
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: getDirectlyObservedTherapyByTherapyQueryKey(therapyId) });
      void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
      handleOnClose();
    },
    onError: () => toast.error('Beim Speichern der DOT ist ein Fehler aufgetreten'),
  });

  useEffect(() => {
    if (open) {
      reset({
        until: entry.until ?? executor?.until ?? '',
        frequenz: toDotFrequencyDisplayLabel(entry.frequenz),
      });
    }
  }, [open, entry, executor?.until, reset]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 550 }}>
        <ModalClose />
        <Typography level={'h4'}>DOT beenden</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            updateDirectlyObservedTherapyMutation.mutate({
              patientId,
              dotId: entry.id,
              data: {
                until: data.until,
                frequenz: toDotFrequencyPayload(data.frequenz),
              },
            }),
          )}>
          <Stack gap={2}>
            <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>
                  <Typography>Startdatum</Typography>
                </FormLabel>
                <Typography>{startDate ? formatDateWithGermanMonth(startDate) : '-'}</Typography>
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={!!errors.until}>
                <FormLabel>
                  <Typography>Enddatum</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="until"
                  rules={{
                    required: false,
                    min: startDateForMin ? { value: startDateForMin, message: 'range' } : undefined,
                  }}
                  min={startDateForMin}
                />
                {errors.until && <FormHelperText>Enddatum muss nach dem Startdatum liegen</FormHelperText>}
              </FormControl>
            </Stack>
            <FormControl>
              <FormLabel>
                <Typography>DOT Frequenz</Typography>
              </FormLabel>
              <Controller
                name="frequenz"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    freeSolo
                    value={value ?? ''}
                    inputValue={value ?? ''}
                    onChange={(_, newValue) => onChange(newValue ?? '')}
                    onInputChange={(_, newInputValue) => onChange(newInputValue)}
                    options={DOT_FREQUENCY_OPTIONS.map((option) => option.label)}
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
            <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
              {applicant && (
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>
                    <Typography>Auftraggeber</Typography>
                  </FormLabel>
                  <PatientAddressDisplay patientAddress={applicant} />
                </FormControl>
              )}
              {executor && (
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>
                    <Typography>Durchführer</Typography>
                  </FormLabel>
                  <PatientAddressDisplay patientAddress={executor} />
                </FormControl>
              )}
            </Stack>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={updateDirectlyObservedTherapyMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
